import { parseISO } from "date-fns";
import { Pencil } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Button } from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import { GetDipDetailDocument } from "~/features/dataImportProjects/gqls";
import { useDipLabels } from "~/features/dataImportProjects/hooks/useDipLabels";
import type { DipProjectInfoUI } from "~/features/dataImportProjects/types";
import { badgeToneFor } from "~/features/dataImportProjects/utils/tone";
import { DipProjectInfoEditForm } from "./DipProjectInfoEditForm";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

interface DipProjectInfoProps {
  info: DipProjectInfoUI;
  projectId: string;
}

export function DipProjectInfo({ info, projectId }: DipProjectInfoProps) {
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const labels = useDipLabels();
  const [editing, setEditing] = useState(false);
  const fmt = (iso: string | null) => (iso ? t("date.short", { date: parseISO(iso) }) : "—");

  useEffect(() => {
    if (isReadOnly) setEditing(false);
  }, [isReadOnly]);

  const rows: { key: string; value: ReactNode }[] = [
    { key: "info.status", value: <Badge tone={badgeToneFor[info.statusTone]}>{info.status}</Badge> },
    { key: "info.updated", value: fmt(info.updatedAtISO) },
    { key: "info.startDate", value: fmt(info.startAtISO) },
    { key: "info.endDate", value: fmt(info.endAtISO) },
  ];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-16-bold">{labels.projectInfo}</h3>
        {!editing && (
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("ui.edit")}
              disabled={isReadOnly}
              onClick={() => {
                if (isReadOnly) return;
                setEditing(true);
              }}
            >
              <Pencil className="size-4" />
            </Button>
          </DipReadOnlyTooltip>
        )}
      </div>
      {editing ? (
        <DipProjectInfoEditForm
          projectId={projectId}
          readOnly={isReadOnly}
          refetchQueriesOnEdit={[{ query: GetDipDetailDocument, variables: { id: projectId } }]}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <dl className="grid gap-3">
          {rows.map((row) => (
            <div key={row.key} className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
              <dt className="text-12-regular text-fg-3">{labels[row.key] ?? row.key}</dt>
              <dd className="text-14-regular text-fg-1">{row.value}</dd>
            </div>
          ))}
          <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
            <dt className="text-12-regular text-fg-3">{labels["info.users"]}</dt>
            <dd className="flex flex-wrap gap-1.5">
              {info.users.map((user) => (
                <Badge key={user}>{user}</Badge>
              ))}
            </dd>
          </div>
          {info.description && (
            <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
              <dt className="text-12-regular text-fg-3">{labels["info.description"]}</dt>
              <dd className="text-14-regular text-fg-1">{info.description}</dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}
